import api from '@/api';
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const ITEMS_PER_PAGE = 3;

export function useLevelOne() {
  const route = useRoute();
  const router = useRouter();
  const { locale } = useI18n();
  const ferramenta = ref({ nome: '', descricao: '' });
  const ferramentaNome = ref('');
  const isLoading = ref(false);
  const versaoConteudo = ref('');
  const versaoExcerpt = ref('');
  const tempoLeituraMin = ref(null);
  const subgroupsNivel2 = ref([]);

  const currentPage = computed({
    get: () => Math.max(1, parseInt(route.query.page) || 1),
    set: (val) => {
      router.replace({ query: { ...route.query, page: val > 1 ? val : undefined } });
    },
  });

  const paginatedSubgroups = computed(() => {
    const start = (currentPage.value - 1) * ITEMS_PER_PAGE;
    return subgroupsNivel2.value.slice(start, start + ITEMS_PER_PAGE);
  });

  const getCurrentLanguage = () => {
    return localStorage.getItem('selectedLanguage') || locale.value || 'pt';
  };

  const fetchToolById = async (id) => {
    if (isLoading.value) return;

    try {
      isLoading.value = true;
      const response = await api.getToolById(id);

      if (response.data && response.data.nivel === 1) {
        ferramenta.value = response.data;
        ferramentaNome.value = response.data.nome;

        const currentLang = getCurrentLanguage();
        if (response.data.idioma !== currentLang) {
          await fetchToolByLanguage(response.data.nome, currentLang);
        } else {
          await fetchConteudoDaVersao(response.data.id, currentLang);
          await fetchSubgroupsNivel2(response.data.id, currentLang);
        }
      } else {
        ferramenta.value = { nome: '', descricao: 'Ferramenta não encontrada.' };
        console.warn('Ferramenta não é de nível zero ou não encontrada.');
      }
    } catch (error) {
      console.error('Erro ao buscar ferramenta por ID:', error);
      ferramenta.value = { nome: '', descricao: 'Erro ao carregar ferramenta.' };
    } finally {
      isLoading.value = false;
    }
  };

  const fetchToolByLanguage = async (nome, idioma) => {
    if (!nome || isLoading.value) return;

    try {
      isLoading.value = true;
      const params = { nome: nome, idioma: idioma };
      const response = await api.searchTools(params);
      const filteredTools = response.data.filter(tool =>
        tool.nome === nome && tool.idioma === idioma && tool.nivel === 1
      );

      const newTool = filteredTools.length > 0 ? filteredTools[0] : null;

      if (newTool) {
        ferramenta.value = newTool;
        await fetchConteudoDaVersao(newTool.id, idioma);
        await fetchSubgroupsNivel2(newTool.id, idioma);
      } else {
        console.warn(`Ferramenta não encontrada com nome '${nome}' e idioma '${idioma}'.`);
        ferramenta.value = {
          ...ferramenta.value,
          descricao: `Tradução não disponível para o idioma '${idioma}'`
        };
        versaoConteudo.value = 'Tradução não disponível.';
      }
    } catch (error) {
      console.error('Erro ao buscar ferramenta por idioma:', error);
      ferramenta.value = {
        ...ferramenta.value,
        descricao: 'Erro ao carregar tradução'
      };
      versaoConteudo.value = 'Erro ao carregar conteúdo.';
    } finally {
      isLoading.value = false;
    }
  };

  const fetchConteudoDaVersao = async (idFerramenta, idioma) => {
    try {
      const response = await api.getConteudoVersoesPorFerramenta(idFerramenta, idioma);
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        const versao = response.data[0];
        versaoConteudo.value = versao.conteudo || 'Sem conteúdo disponível.';
        versaoExcerpt.value = versao.excerpt || '';
        tempoLeituraMin.value = versao.tempo_leitura_min || null;
      } else {
        versaoConteudo.value = 'Conteúdo não encontrado.';
        versaoExcerpt.value = '';
        tempoLeituraMin.value = null;
      }
    } catch (error) {
      console.error('Erro ao buscar conteúdo da versão:', error);
      versaoConteudo.value = 'Erro ao carregar conteúdo.';
      versaoExcerpt.value = '';
      tempoLeituraMin.value = null;
    }
  };

  const fetchSubgroupsNivel2 = async (idFerramentaPrincipal, idioma) => {
    try {
      const response = await api.searchTools({ idioma: idioma });
      const allTools = response.data || [];
      const filteredSubgroups = allTools.filter(tool =>
        tool.nivel === 2 && tool.ferramenta_pai_id === idFerramentaPrincipal
      ).sort((a, b) => a.nome.localeCompare(b.nome));

      subgroupsNivel2.value = filteredSubgroups;
    } catch (error) {
      console.error('Erro ao buscar e filtrar subgrupos Nivel 2:', error);
      subgroupsNivel2.value = [];
    }
  };

  const navigateToLevelTwo = (subgroupId) => {
    try {
      router.push({
        name: 'LevelTwo',
        params: { id: subgroupId },
      });
    } catch (error) {
      console.error('Erro ao navegar para LevelTwo:', error);
    }
  };

  onMounted(() => {
    const idFerramenta = route.params.id;
    if (idFerramenta) {
      fetchToolById(idFerramenta);
    } else {
      console.warn('ID da ferramenta não fornecido na rota.');
    }
  });

  watch(() => route.params.id, (newId, oldId) => {
    if (newId && newId !== oldId) {
      router.replace({ query: {} });
      fetchToolById(newId);
    }
  });

  watch(locale, (newLocale) => {
    if (ferramentaNome.value) {
      fetchToolByLanguage(ferramentaNome.value, newLocale);
    }
  });

  return {
    ferramenta,
    ferramentaNome,
    isLoading,
    versaoConteudo,
    versaoExcerpt,
    tempoLeituraMin,
    subgroupsNivel2,
    paginatedSubgroups,
    currentPage,
    ITEMS_PER_PAGE,
    navigateToLevelTwo,
  };
}

export default {
  name: 'LevelOne',
  setup() {
    return useLevelOne();
  }
};
